/*
  Quiénes somos — page-about.js
  - Hero: the sun rises from behind the horizon on load, then keeps rising
    slowly as the visitor scrolls into the page. With the layered landscape,
    the layers drift at different speeds on scroll (parallax).
  - Journey (name origin + timeline): the frame is pinned and the scroll moves
    the story through it; the frame's background climbs from the soil to the
    sky with the active item.
  - Family & purpose: the sheet rises over the group photo, then holds
    still while the scroll swaps familia → filosofía → misión (blur
    crossfade driven by the scroll, no timer).
  - Xeridas hoy: numbers count up on first view; "·" lists roll like a drum.
  Uses GSAP + ScrollTrigger (via XeridasMotion). Off with reduced motion: the
  journey stacks normally (CSS default without the .is-pinned class).
*/
(function () {
  if (window.__xeridasAbout) return;
  window.__xeridasAbout = true;

  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function scrollTrigger() {
    if (window.XeridasMotion?.useScrollTrigger) return window.XeridasMotion.useScrollTrigger();
    if (window.gsap && window.ScrollTrigger) {
      window.gsap.registerPlugin(window.ScrollTrigger);
      return window.ScrollTrigger;
    }
    return null;
  }

  function initHero(scope) {
    scope.querySelectorAll('[data-about-hero]').forEach((hero) => {
      if (hero.classList.contains('about-hero--layers')) {
        initLayers(hero);
        return;
      }
      const sun = hero.querySelector('[data-about-sun]');
      if (!sun || hero.dataset.aboutReady || reduced() || !window.gsap) return;
      hero.dataset.aboutReady = 'true';

      // Sunrise on load: from half-hidden below the horizon to its resting place.
      window.gsap.fromTo(
        sun,
        { yPercent: -10, opacity: 0.4 },
        { yPercent: -50, opacity: 1, duration: 2.4, ease: 'power2.out', delay: 0.2 }
      );

      // Keeps rising as the page scrolls away from the hero.
      const ST = scrollTrigger();
      if (!ST) return;
      window.gsap.to(sun, {
        y: () => -hero.offsetHeight * 0.08,
        ease: 'none',
        scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true, invalidateOnRefresh: true },
      });
    });
  }

  // Layered landscape: CSS assembles the layers on load. Here each depth
  // drifts down as the hero scrolls away, far layers more than near ones,
  // so the landscape separates in depth. The foreground stays put.
  function initLayers(hero) {
    if (hero.dataset.aboutReady || reduced() || !window.gsap) return;
    const ST = scrollTrigger();
    if (!ST) return;
    hero.dataset.aboutReady = 'true';

    hero.querySelectorAll('[data-depth]').forEach((layer) => {
      const depth = parseFloat(layer.dataset.depth) || 0;
      window.gsap.to(layer, {
        y: () => hero.offsetHeight * depth,
        ease: 'none',
        scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true, invalidateOnRefresh: true },
      });
    });
  }

  // Journey: the frame is pinned for as long as the track takes to pass
  // through it, and the page scroll moves the track (1px of scroll = 1px of
  // travel). The item crossing the middle of the frame is the active one; the
  // frame takes its stage (soil → sky) and the line fills up to it.
  // Touch screens: the journey is not pinned (initJourneyFlow). A pinned
  // frame moved by script on every scroll frame could not keep up with the
  // finger on a phone, and its half-screen runway after the last chapter
  // read as an empty gap before the next section.
  function initJourney(scope) {
    scope.querySelectorAll('[data-about-journey]').forEach((section) => {
      if (section.dataset.aboutReady || reduced()) return;
      const frame = section.querySelector('[data-journey-frame]');
      const track = section.querySelector('[data-journey-track]');
      const timeline = section.querySelector('.about-journey__timeline');
      const items = [...section.querySelectorAll('[data-journey-item]')];
      if (!frame || !track || !items.length) return;
      if (window.matchMedia('(pointer: coarse)').matches) {
        initJourneyFlow(section, frame, timeline, items);
        return;
      }
      const ST = window.gsap ? scrollTrigger() : null;
      if (!ST) return;
      section.dataset.aboutReady = 'true';
      section.classList.add('is-pinned');

      const distance = () => Math.max(0, track.offsetHeight - frame.clientHeight);
      let active = null;

      const update = () => {
        const box = frame.getBoundingClientRect();
        const middle = box.top + box.height / 2;

        // Item under the middle line, or the closest one to it.
        let next = items[0];
        let nearest = Infinity;
        items.forEach((item) => {
          const r = item.getBoundingClientRect();
          const gap = r.top > middle ? r.top - middle : r.bottom < middle ? middle - r.bottom : 0;
          if (gap < nearest) {
            nearest = gap;
            next = item;
          }
        });
        if (next !== active) {
          active?.classList.remove('is-active');
          next.classList.add('is-active');
          frame.dataset.stage = next.dataset.stage || 'soil';
          active = next;
        }

        if (timeline) {
          const t = timeline.getBoundingClientRect();
          const progress = Math.min(1, Math.max(0, (middle - t.top) / t.height));
          timeline.style.setProperty('--journey-progress', progress.toFixed(4));
        }
      };

      // The tween's onUpdate (not the trigger's) so the active item is read on
      // every scrub frame, after the track has actually moved.
      window.gsap.to(track, {
        y: () => -distance(),
        ease: 'none',
        onUpdate: update,
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: () => `+=${distance()}`,
          pin: frame,
          scrub: 0.6,
          invalidateOnRefresh: true,
          onRefresh: update,
        },
      });
      update();

      // Chapter photos change the track's height once they load.
      track.querySelectorAll('img').forEach((img) => {
        if (!img.complete) img.addEventListener('load', () => ST.refresh(), { once: true });
      });
    });
  }

  // Flowing journey (touch): the page scrolls the story natively. An
  // observer on the middle line of the screen picks the active chapter (no
  // layout reads per frame); the line's fill reads one rectangle per frame,
  // only while the timeline is on screen.
  function initJourneyFlow(section, frame, timeline, items) {
    section.dataset.aboutReady = 'true';
    section.classList.add('is-flowing');

    const activate = (item) => {
      if (item.classList.contains('is-active')) return;
      items.forEach((other) => other.classList.toggle('is-active', other === item));
      frame.dataset.stage = item.dataset.stage || 'soil';
    };
    activate(items[0]);

    const middle = new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.isIntersecting && activate(entry.target)),
      { rootMargin: '-50% 0px -50% 0px' }
    );
    items.forEach((item) => middle.observe(item));

    if (!timeline) return;
    timeline.style.setProperty('--journey-progress', 0);
    let ticking = false;
    const fill = () => {
      ticking = false;
      const t = timeline.getBoundingClientRect();
      const progress = Math.min(1, Math.max(0, (window.innerHeight / 2 - t.top) / t.height));
      timeline.style.setProperty('--journey-progress', progress.toFixed(4));
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(fill);
      }
    };
    new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        window.addEventListener('scroll', onScroll, { passive: true });
        fill();
      } else {
        window.removeEventListener('scroll', onScroll);
      }
    }).observe(timeline);
  }

  // Portrait cards: the "+" opens the bio inside the small sheet over the
  // portrait (and turns into "×"). They start closed once JS is here;
  // without JS the bio stays open and the button hidden. Escape closes.
  function initPersonSheets(section) {
    section.querySelectorAll('[data-person]').forEach((card) => {
      const toggle = card.querySelector('[data-person-toggle]');
      if (!toggle) return;
      const set = (open) => {
        card.dataset.open = open ? 'true' : 'false';
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      };
      set(false);
      toggle.hidden = false;
      toggle.addEventListener('click', () => set(card.dataset.open !== 'true'));
      card.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && card.dataset.open === 'true') {
          set(false);
          toggle.focus();
        }
      });
    });
  }

  // Family & purpose (la familia → filosofía → misión, one section).
  // - The sheet rises over the sticky group photo: it grows from 92% to full
  //   size and the photo takes a light veil.
  // - Pinned mode (desktop with motion): the sheet is one screen tall and
  //   sticky (CSS); the runway below it gives the scroll distance, laid out
  //   in pixels as: for each state, a short hold, the state's overflow (it
  //   moves up inside the stage, 1px per px of scroll), another hold; then
  //   a swap to the next state. In a swap the outgoing state fades and blurs
  //   first, the incoming one follows; the title box glides between the two
  //   headings' widths in the middle of it, so the dots hug each heading.
  //   Elements that span two states ("Nuestra") hold still between them.
  //   Hidden states only go transparent, so screen readers keep all three.
  // - Stacked mode (phones, very short screens, reduced motion): no
  //   weighting; the states follow one another with the page's [data-reveal].
  function initPeople(scope) {
    scope.querySelectorAll('[data-about-people]').forEach((section) => {
      if (section.dataset.aboutReady) return;
      section.dataset.aboutReady = 'true';

      initPersonSheets(section);

      const ST = scrollTrigger();
      const gsap = window.gsap;
      const sheet = section.querySelector('[data-people-sheet]');
      const stage = section.querySelector('.about-people__stage');
      const runway = section.querySelector('[data-people-runway]');
      const titlesBox = section.querySelector('[data-people-titles]');
      const titles = titlesBox ? [...titlesBox.querySelectorAll('[data-from]')] : [];
      const panels = [...section.querySelectorAll('[data-panel]')];
      const layers = [...section.querySelectorAll('.about-people__inner [data-from]')];
      const framed = section.classList.contains('about-people--framed');
      const last = panels.length - 1;
      const swapLength = (parseFloat(section.dataset.swapLength) || 100) / 100;

      const clamp01 = (x) => Math.min(1, Math.max(0, x));
      const smooth = (a, b, x) => {
        const t = clamp01((x - a) / (b - a));
        return t * t * (3 - 2 * t);
      };
      const frame = () => parseFloat(getComputedStyle(section).getPropertyValue('--people-frame')) || 0;

      let pinned = false;
      let x = 0; // px travelled along the runway
      let plan = null; // { starts, overs, hold, swap, total }
      let widths = [];

      // u(k): 0 → 1 through the swap from state k to k + 1.
      const swapAt = (k) => clamp01((x - (plan.starts[k] + plan.dwell[k])) / plan.swap);

      const weight = (el) => {
        const from = +el.dataset.from;
        const to = +el.dataset.to;
        const enter = from === 0 ? 1 : smooth(0.45, 1, swapAt(from - 1));
        const exit = to === last ? 1 : 1 - smooth(0, 0.55, swapAt(to));
        return Math.min(enter, exit);
      };

      // How far state i has moved up inside the stage.
      const travel = (i) => Math.min(plan.overs[i], Math.max(0, x - plan.starts[i] - plan.hold));

      // Widest rendered line of each heading, so a heading that wraps still
      // hugs the dots. Client rects include the sheet's rise scale (0.92 →
      // 1), so they are divided back to layout pixels.
      const measureTitles = () => {
        if (!titlesBox) return;
        const row = titlesBox.parentElement;
        const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
        const dots = [...row.querySelectorAll('.about-dot')].reduce((sum, dot) => sum + dot.offsetWidth, 0);
        titlesBox.style.setProperty('--people-title-max', `${row.clientWidth - dots - gap * 2}px`);
        const scale = sheet.getBoundingClientRect().width / sheet.offsetWidth || 1;
        widths = titles.map((title) => {
          const range = document.createRange();
          range.selectNodeContents(title);
          const lines = [...range.getClientRects()].map((rect) => rect.width / scale);
          return Math.ceil(lines.length ? Math.max(...lines) : title.offsetWidth);
        });
      };

      const measurePlan = () => {
        const vh = window.innerHeight;
        const room = stage.clientHeight;
        const hold = Math.round(vh * 0.3);
        const swap = Math.round(vh * swapLength);
        const overs = panels.map((panel) => Math.max(0, Math.ceil(panel.offsetHeight - room)));
        const dwell = overs.map((over) => over + hold * 2);
        const starts = [];
        let at = 0;
        dwell.forEach((d, i) => {
          starts[i] = at;
          at += d + (i < last ? swap : 0);
        });
        plan = { starts, overs, dwell, hold, swap, total: at };
        runway.style.height = `${plan.total}px`;
        // Lenis caches the page's scroll limit: tell it the page grew.
        window.XeridasMotion?.lenis?.resize?.();
      };

      const paint = () => {
        if (!pinned || !plan) return;
        let phase = 0;
        let best = -1;
        const weights = new Map(layers.map((el) => [el, weight(el)]));

        layers.forEach((el) => {
          const w = weights.get(el);
          const moving = w > 0.001 && w < 0.999;
          el.style.opacity = w.toFixed(3);
          el.style.filter = moving ? `blur(${((1 - w) * 8).toFixed(2)}px)` : '';
        });

        panels.forEach((panel, i) => {
          const w = weights.get(panel);
          const lift = (1 - w) * 12;
          panel.style.transform = `translate3d(0, ${(lift - travel(i)).toFixed(1)}px, 0)`;
          panel.style.pointerEvents = w < 0.5 ? 'none' : '';
          if (w > best) {
            best = w;
            phase = i;
          }
        });
        section.dataset.phase = phase;

        // Edge fades while the active state has content above / below.
        const moved = travel(phase);
        const left = plan.overs[phase] - moved;
        section.style.setProperty('--people-fade-top', `${Math.min(32, moved).toFixed(1)}px`);
        section.style.setProperty('--people-fade-bottom', `${Math.min(48, left).toFixed(1)}px`);

        // Title box: from one heading's width to the next, in the middle of
        // each swap (where both headings are faint), eased.
        if (titlesBox && widths.length) {
          let width = widths[0];
          for (let k = 0; k < last; k++) {
            width += (widths[k + 1] - width) * smooth(0.25, 0.75, swapAt(k));
          }
          titlesBox.style.width = `${width.toFixed(1)}px`;
        }

        // The route draws itself once the mission has arrived.
        const arrived = x - plan.starts[last];
        section.style.setProperty('--people-route', smooth(0, plan.hold * 1.5, arrived).toFixed(3));
      };

      const clear = () => {
        layers.forEach((el) => {
          ['opacity', 'filter', 'transform', 'pointer-events'].forEach((prop) => el.style.removeProperty(prop));
        });
        ['--people-route', '--people-fade-top', '--people-fade-bottom'].forEach((prop) => section.style.removeProperty(prop));
        titlesBox?.style.removeProperty('width');
        runway.style.removeProperty('height');
        section.dataset.phase = '0';
        plan = null;
      };

      // Pinned on desktop with motion, unless the screen is so short that the
      // stage under the header would be a sliver.
      const decide = () => {
        if (!section.isConnected) return;
        const want = !reduced() && !!ST && window.matchMedia('(min-width: 750px)').matches;
        section.classList.toggle('is-pinned', want);
        pinned = want && stage.clientHeight >= 280;
        section.classList.toggle('is-pinned', pinned);
        if (pinned) {
          measureTitles();
          measurePlan();
          paint();
        } else {
          clear();
        }
      };

      decide();
      if (!ST || !gsap) return;

      // The sheet is stuck while the runway passes under it: from the moment
      // the runway's top sits at the sheet's bottom, for the runway's height.
      ST.create({
        trigger: runway,
        start: () => `top ${frame() + sheet.offsetHeight}px`,
        end: () => `+=${Math.max(1, runway.offsetHeight)}`,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          x = self.progress * (plan?.total || 0);
          paint();
        },
        onRefresh: (self) => {
          x = self.progress * (plan?.total || 0);
          paint();
        },
      });

      // Where the sheet sits before it sticks (the section is the trigger:
      // a sticky element would be measured where it is stuck).
      const sheetOffset = () => {
        const photo = section.querySelector('.about-people__photo');
        const inFlow = photo && getComputedStyle(photo).position !== 'absolute';
        return (
          parseFloat(getComputedStyle(section).paddingTop) +
          (inFlow ? photo.offsetHeight : 0) +
          parseFloat(getComputedStyle(sheet).marginTop)
        );
      };

      if (!reduced()) {
        const rise = {
          trigger: section,
          start: () => `top+=${sheetOffset()} bottom`,
          end: () => `top+=${sheetOffset()} 30%`,
          scrub: 0.6,
          invalidateOnRefresh: true,
        };
        gsap.fromTo(sheet, { scale: 0.92 }, { scale: 1, ease: 'none', scrollTrigger: rise });
        if (framed) {
          gsap.fromTo(section, { '--people-veil': 0 }, { '--people-veil': 0.3, ease: 'none', scrollTrigger: { ...rise } });
        }
      }

      // Headings change width when the display font arrives: re-measure then
      // (the dots would otherwise sit on the letters).
      if (titlesBox && window.ResizeObserver) {
        const ro = new ResizeObserver(() => {
          if (!pinned) return;
          measureTitles();
          paint();
        });
        titles.forEach((title) => ro.observe(title));
      }

      // Every refresh (resize, fonts) decides the mode first, then measures;
      // the first one also accounts for the runway's height.
      ST.addEventListener('refreshInit', decide);
      document.fonts?.ready.then(() => ST.refresh());
      ST.refresh();
    });
  }

  // Xeridas hoy: figures with a number count up from 0 the first time they
  // come into view (prefix / suffix stay: "+15 años"); "·" lists roll
  // vertically in a one-line window, one name at a time (center / prev /
  // next, the story carousel's modular logic turned vertical; a name
  // wrapping round is parked without animating across); other words ("Q
  // Grader") drop in letter by letter, like a wave, on first view and again
  // on hover. The list turns every 2.6 s from its first view, without pauses.
  // Screen readers get the final value / the written text. Reduced motion:
  // values show as written.
  function initToday(scope) {
    scope.querySelectorAll('[data-about-today]').forEach((section) => {
      if (section.dataset.aboutReady) return;
      if (reduced()) {
        // No counting: a stepped value ("80+ 85+ 90+") shows its last figure.
        section.dataset.aboutReady = 'true';
        section.querySelectorAll('[data-today-effect="count"]').forEach(showLastStep);
        return;
      }
      section.dataset.aboutReady = 'true';

      section.querySelectorAll('[data-today-effect="count"]').forEach(initCount);
      section.querySelectorAll('[data-today-effect="roulette"]').forEach(initWheel);
      section.querySelectorAll('[data-today-effect="wave"]').forEach(initWave);
    });
  }

  function initWave(value) {
    const text = value.textContent.trim();
    if (!text) return;

    value.textContent = '';
    const final = document.createElement('span');
    final.className = 'visually-hidden';
    final.textContent = text;
    const letters = document.createElement('span');
    letters.className = 'about-today__letters';
    letters.setAttribute('aria-hidden', 'true');
    [...text].forEach((char, i) => {
      const letter = document.createElement('span');
      letter.className = 'about-today__letter';
      letter.style.setProperty('--i', i);
      letter.textContent = char;
      letters.append(letter);
    });
    value.append(final, letters);
    // A note after the value (×3) waits for the last letter (page-about.css).
    value.parentElement.style.setProperty('--letters', text.length);
    value.classList.add('is-wave-ready');

    const play = () => {
      value.classList.remove('is-waving');
      void value.offsetWidth;
      value.classList.add('is-waving');
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        play();
        // After the first wave, hovering the figure plays it again.
        (value.closest('.about-today__stat') || value.parentElement).addEventListener('pointerenter', play);
      },
      { threshold: 0.6 }
    );
    io.observe(value);
  }

  // The number counts up from 0 ("+15 años"). A value with several numbers
  // ("80+ 85+ 90+") shows one figure: it counts up to the first, then every
  // 3.5 s the next one counts up from 0, looping back to the first; the
  // prefix / suffix around the first number stays put.
  function showLastStep(value) {
    const text = value.textContent.trim();
    const numbers = text.match(/\d+/g);
    if (!numbers || numbers.length < 2) return;
    const words = text.split(/\s+/);
    value.textContent = '';
    const final = document.createElement('span');
    final.className = 'visually-hidden';
    final.textContent = text;
    const shown = document.createElement('span');
    shown.setAttribute('aria-hidden', 'true');
    shown.textContent = words[words.length - 1];
    value.append(final, shown);
  }

  function initCount(value) {
    const text = value.textContent.trim();
    const numbers = text.match(/\d+/g);
    if (!numbers) return;
    const first = text.match(/\d+/);
    const before = text.slice(0, first.index);
    const rest = text.slice(first.index + first[0].length);
    const after = numbers.length > 1 ? rest.slice(0, rest.search(/\d/)).trim() : rest;
    const steps = numbers.map((n) => parseInt(n, 10));

    // The written value for screen readers; the counting copy is hidden from them.
    value.textContent = '';
    const final = document.createElement('span');
    final.className = 'visually-hidden';
    final.textContent = text;
    const shown = document.createElement('span');
    shown.setAttribute('aria-hidden', 'true');
    const number = document.createElement('span');
    number.className = 'about-today__number';
    number.textContent = '0';
    shown.append(before, number, after);
    value.append(final, shown);

    // Counts from → to with an ease-out, then calls done.
    const tween = (from, to, duration, done) => {
      const start = performance.now();
      const step = (now) => {
        // A frame's timestamp can be a little older than start: clamp at 0,
        // or the figure flashes a negative number on its first frame.
        const t = Math.min(1, Math.max(0, (now - start) / duration));
        number.textContent = Math.round(from + (to - from) * (1 - Math.pow(1 - t, 4)));
        if (t < 1) requestAnimationFrame(step);
        else done?.();
      };
      requestAnimationFrame(step);
    };

    // Several numbers: each one holds for 3.5 s, then the next one counts up
    // from 0 exactly like the first view did (80 → 0…85 → 0…90 → 0…80), in a
    // loop.
    const HOLD = 3500;
    const COUNT = 1800;
    const loop = (index) => {
      setTimeout(() => {
        const next = (index + 1) % steps.length;
        tween(0, steps[next], COUNT, () => loop(next));
      }, HOLD);
    };

    const run = () => {
      // Holds the widest figure (Nippo's digits are proportional; measured
      // now that the font is in), so nothing around it shifts.
      const widths = steps.map((n) => {
        number.textContent = n;
        return number.getBoundingClientRect().width;
      });
      number.style.minWidth = `${Math.max(...widths)}px`;
      // Sticks to its prefix ("+15") or to its suffix ("80+") while counting.
      number.style.textAlign = before.trim() ? 'left' : 'right';
      number.textContent = '0';

      tween(0, steps[0], COUNT, steps.length > 1 ? () => loop(0) : null);
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        run();
      },
      { threshold: 0.6 }
    );
    io.observe(value);
  }

  function initWheel(value) {
    const wheel = value.querySelector('[data-today-wheel]');
    let items = wheel ? [...wheel.querySelectorAll('[data-wheel-item]')] : [];
    if (items.length < 2) return;

    // At least five names on the drum, so the one wrapping round is always
    // off stage (hidden-prev → hidden-next); short lists are repeated.
    const names = items.map((item) => item.textContent);
    while (items.length < 5) {
      names.forEach((name) => {
        const copy = items[0].cloneNode(false);
        copy.textContent = name;
        wheel.append(copy);
        items.push(copy);
      });
    }

    // A name wider than its column ("República Checa" in a quarter of the
    // page) is set smaller, just enough to fit on the one line; the others
    // keep the figures' size. Measured once the fonts are in and again
    // whenever the column changes width.
    const column = value.closest('dd') || value.parentElement;
    const fit = () => {
      const room = column.clientWidth;
      if (!room) return;
      items.forEach((item) => {
        item.style.fontSize = '';
        const width = item.getBoundingClientRect().width;
        // 2% margin: tracking and kerning don't shrink exactly in proportion.
        item.style.fontSize = width > room ? `${Math.max(0.6, (room / width) * 0.98).toFixed(3)}em` : '';
      });
    };
    (document.fonts ? document.fonts.ready : Promise.resolve()).then(fit);
    new ResizeObserver(fit).observe(column);

    const total = items.length;
    let current = 0;

    const posFor = (index) => {
      let offset = (((index - current) % total) + total) % total;
      if (offset > total / 2) offset -= total;
      if (offset === 0) return 'center';
      if (offset === -1) return 'prev';
      if (offset === 1) return 'next';
      return offset < 0 ? 'hidden-prev' : 'hidden-next';
    };

    const render = () => {
      items.forEach((item, index) => {
        const next = posFor(index);
        const wraps =
          (item.dataset.pos === 'hidden-prev' && next === 'hidden-next') ||
          (item.dataset.pos === 'hidden-next' && next === 'hidden-prev');
        if (wraps) {
          item.classList.add('is-parked');
          item.dataset.pos = next;
          void item.offsetWidth;
          item.classList.remove('is-parked');
        } else {
          item.dataset.pos = next;
        }
      });
    };

    const advance = () => {
      current = (current + 1) % total;
      render();
    };

    // First view: the list turns right away, together with the count-ups and
    // the letter wave (same 0.6 threshold), so all four figures move at once;
    // then it keeps turning at a steady pace.
    const kick = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        kick.disconnect();
        advance();
        setInterval(advance, 4000);
      },
      { threshold: 0.6 }
    );
    kick.observe(value);

    items.forEach((item) => item.classList.add('is-parked'));
    render();
    value.classList.add('is-rolling');
    requestAnimationFrame(() => requestAnimationFrame(() => items.forEach((item) => item.classList.remove('is-parked'))));
  }

  // Links from the landing.
  // - #comenzar-recorrido ("Conoce nuestra historia"): the page opens at the
  //   top; once the layered landscape has finished assembling (its own CSS
  //   animations, read with getAnimations: no polling), the page glides to
  //   the journey as if "Comienza el recorrido" had been clicked (a slower
  //   2 s glide), unless the visitor has already started scrolling.
  // - Any other anchor (#familia, #xeridas-hoy): the about-hero inline
  //   script has hidden the page (html.is-about-deeplink); once ScrollTrigger
  //   has laid out the pinned runways, the page is placed on the section's
  //   top and shown. #familia lands on the group photo alone, before the
  //   sheet rises.
  function initAutoTour() {
    if (window.location.hash !== '#comenzar-recorrido') return;
    history.replaceState(null, '', window.location.pathname + window.location.search);
    const hero = document.querySelector('[data-about-hero]');
    const cue = hero?.querySelector('a.about-hero__cue');
    if (!cue) return;
    window.scrollTo(0, 0);

    const assembling = reduced()
      ? []
      : hero.getAnimations({ subtree: true }).filter((animation) => {
          const end = animation.effect?.getComputedTiming().endTime;
          return Number.isFinite(end);
        });
    Promise.all(assembling.map((animation) => animation.finished.catch(() => {}))).then(() => {
      setTimeout(() => {
        if (window.scrollY > 40) return;
        // Same as the cue's click, with a calmer glide than a manual click.
        const lenis = window.XeridasMotion?.lenis;
        const journey = document.querySelector(cue.hash);
        if (!lenis || !journey) {
          cue.click();
          return;
        }
        const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
        lenis.scrollTo(journey, { duration: 2, easing: ease });
        history.pushState(null, '', cue.hash);
      }, reduced() ? 0 : 600);
    });
  }

  function initDeepLink() {
    const root = document.documentElement;
    if (!root.classList.contains('is-about-deeplink')) return;
    const show = () => root.classList.remove('is-about-deeplink');

    let target = null;
    try {
      target = document.querySelector(decodeURIComponent(window.location.hash));
    } catch (error) {
      target = null;
    }
    if (!target) {
      show();
      return;
    }

    // Not window "load": that waited for every image on the page (the hero
    // layers, all portraits) and kept the page blank for seconds on a slow
    // connection. Placement only needs the pinned runways (laid out by now),
    // the fonts, and the section's first photo (the family group photo, lazy:
    // fetched now so the page doesn't open on an empty frame). Images that
    // arrive later are handled by the re-placement below.
    const photo = target.querySelector('img');
    if (photo) photo.loading = 'eager';
    const decoded = photo?.decode ? photo.decode().catch(() => {}) : null;
    const ready = Promise.all([decoded, document.fonts ? document.fonts.ready : null]);
    const timeout = new Promise((resolve) => setTimeout(resolve, 1200));

    const place = () => {
      const y = Math.round(target.getBoundingClientRect().top + window.scrollY);
      const lenis = window.XeridasMotion?.lenis;
      if (lenis) lenis.scrollTo(y, { immediate: true, force: true });
      else window.scrollTo(0, y);
    };

    Promise.race([ready, timeout]).then(() => {
      const ST = scrollTrigger();
      ST?.refresh();
      place();
      requestAnimationFrame(() => requestAnimationFrame(show));

      // A late re-measure (an image above loading) would move the section:
      // keep it in place for a moment, until the visitor takes over.
      if (!ST) return;
      const stop = () => {
        ST.removeEventListener('refresh', place);
        ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach((type) => window.removeEventListener(type, stop));
      };
      ST.addEventListener('refresh', place);
      ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach((type) =>
        window.addEventListener(type, stop, { once: true, passive: true })
      );
      setTimeout(stop, 2500);
    });
  }

  function init(scope = document) {
    initHero(scope);
    initJourney(scope);
    initPeople(scope);
    initToday(scope);
  }

  function boot() {
    init();
    initDeepLink();
    initAutoTour();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
    window.XeridasMotion?.refresh?.();
  });

  // Theme editor: drop the pinned journey's triggers before the section is replaced.
  document.addEventListener('shopify:section:unload', (event) => {
    window.ScrollTrigger?.getAll().forEach((trigger) => {
      if (event.target.contains(trigger.trigger)) trigger.kill(true);
    });
  });
})();
